'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ToastContainer } from 'react-toastify';

/**
 * Renders ToastContainer via a portal directly into document.body.
 *
 * Why a portal?
 *   backdrop-filter / filter / will-change / isolation on any ancestor
 *   creates a new stacking context that traps position:fixed children —
 *   even with z-index:99999. Portaling to document.body escapes every
 *   stacking context in the app tree entirely.
 *
 * Why useState+useEffect (not just createPortal inline)?
 *   document.body only exists on the client. The null-return on the
 *   first render prevents Next.js hydration mismatches.
 *
 * Why does this component live inside Providers (not layout.jsx)?
 *   Client Components that are direct children of a Server Component
 *   boundary (like the root layout <body>) can be re-mounted when the
 *   Server Component re-renders on navigation. Providers is a stable
 *   Client root — nothing in the navigation cycle causes it to remount,
 *   so ToastContainer stays alive for the full browser session.
 */
export default function ToastHost() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    // No cleanup: intentionally keep the toast host alive for the
    // full client session regardless of component unmount.
  }, []);

  if (!mounted) return null;

  return createPortal(
    <ToastContainer
      position="top-right"
      autoClose={4000}
      newestOnTop
      closeOnClick
      pauseOnFocusLoss={false}
      draggable
      pauseOnHover
    />,
    document.body,
  );
}
